/**
 * A hub (v7 Part E; CORE-1): one menu item whose merged screens are tabs. Each tab renders the existing screen, unchanged,
 * inside the hub's frame; the tab is held in the address (?tab=<screen id>) so a link, a refresh and Back keep it.
 *
 * A screen shown inside a hub sees `useEmbedded() === true`: its <Page> drops its own title row's heading, so the page
 * reads Hub name › tab, never two titles.
 */
import { createContext, Suspense, useContext, type ReactNode } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { HUBS, MOVED, NAV_SCREENS, menuLabel, movedPath, screenById, workspaceOf, type HubTab } from '@rtcom/domain';
import { useSession } from '../lib/session';
import { Tabs } from '../ui/navigation';
import { LoadingRows, Panel } from '../ui/primitives';
import { toastAfterNavigation } from '../ui/toast';

const Embedded = createContext(false);
/** True inside a hub's tab: the screen is one part of a page that already has its title. */
export const useEmbedded = () => useContext(Embedded);

export function TabFrame({ title, subtitle, tabs, active, onChange, children }: {
  title: string; subtitle?: string; tabs: { id: string; label: string }[]; active: string; onChange: (id: string) => void; children: ReactNode;
}) {
  return (
    <div className="flex min-h-full flex-col">
      <div className="px-6 pt-6">
        <h1 className="text-title">{title}</h1>
        {subtitle ? <p className="mt-1 max-w-prose text-body text-ink-muted">{subtitle}</p> : null}
      </div>
      <div className="px-6 pt-3"><Tabs tabs={tabs} active={active} onChange={onChange} /></div>
      <Embedded.Provider value>
        <Suspense fallback={<div className="px-6 pt-6"><Panel><LoadingRows /></Panel></div>}>{children}</Suspense>
      </Embedded.Provider>
    </div>
  );
}

export function HubScreen({ hubId, render }: { hubId: string; render: (screenId: string) => ReactNode }) {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const hub = screenById(hubId);
  const tabs = (HUBS[hubId] ?? []).filter((t: HubTab) => { const s = screenById(t.screen); return s && can(s.permission); });
  const active = tabs.find((t) => t.screen === params.get('tab'))?.screen ?? tabs[0]?.screen ?? '';
  if (!hub) return null;
  return (
    <TabFrame title={menuLabel(hub)} subtitle={hub.description} active={active}
      tabs={tabs.map((t) => ({ id: t.screen, label: t.label }))}
      onChange={(id) => setParams((p) => { const n = new URLSearchParams(p); n.set('tab', id); return n; }, { replace: true })}>
      {active ? render(active) : null}
    </TabFrame>
  );
}

/** A moved screen's old address: to its new home, saying so once (Part E: kept for 60 days). */
export function MovedRedirect({ screenId }: { screenId: string }) {
  const { search } = useLocation();
  const moved = MOVED[screenId];
  const to = moved ? screenById(moved.to) : undefined;
  if (!moved || !to) return null;
  const ws = workspaceOf(to.id);
  const tabLabel = (HUBS[to.id] ?? []).find((t) => t.screen === moved.tab)?.label;
  toastAfterNavigation(`This page moved to ${ws ? `${ws.label} › ` : ''}${menuLabel(to)}${tabLabel ? ` › ${tabLabel}` : ''}.`);
  const params = new URLSearchParams(search);
  if (moved.tab) params.set('tab', moved.tab);
  const q = params.toString();
  return <Navigate to={`${to.path}${q ? `?${q}` : ''}`} replace />;
}

/** An old /money address (Finance Phase 0): to the same page under /finance, keeping ?query and #hash, saying so once. */
export function PathMovedRedirect() {
  const { pathname, search, hash } = useLocation();
  const to = movedPath(pathname);
  if (!to) return <Navigate to="/" replace />;
  const target = NAV_SCREENS.find((x) => x.path === to);
  toastAfterNavigation(target ? `This page moved to Finance › ${menuLabel(target)}.` : 'This page moved to Finance.');
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/** The screens of the registry that are not built: their addresses lead to the Roadmap, which says when. */
export function PlannedRedirect({ screenId }: { screenId: string }) {
  const s = NAV_SCREENS.find((x) => x.id === screenId);
  toastAfterNavigation(`${s ? menuLabel(s) : 'That page'} is on the Roadmap; it is not part of the ERP yet.`);
  return <Navigate to={`/admin/roadmap#${screenId}`} replace />;
}
